import path from "node:path";

import type { Address, Hash, Hex } from "viem";

/** Absolute path to the devenv package root */
const PACKAGE_ROOT = path.resolve(import.meta.dirname, "..");

/** Addresses deployed in Phase 1: Foundation */
export interface FoundationAddresses {
  weth9: Address;
  permit2: Address;
}

/** Addresses deployed in Phase 2: Uniswap V2 */
export interface V2Addresses {
  factory: Address;
  router02: Address;
  initCodePairHash: Hex;
}

/** Addresses deployed in Phase 3: Uniswap V3 */
export interface V3Addresses {
  factory: Address;
  router: Address;
  quoterV2: Address;
  nonfungiblePositionManager: Address;
  nonfungibleTokenPositionDescriptor: Address;
  tokenDescriptor: Address;
  tickLens: Address;
  migrator: Address;
  staker: Address;
  swapRouter02: Address;
  /** Factory owner (initially deployer) */
  owner: Address;
}

/** Addresses deployed in Phase 4: Uniswap V4 */
export interface V4Addresses {
  poolManager: Address;
  positionDescriptor: Address;
  positionManager: Address;
  quoter: Address;
  stateView: Address;
  /** Test helpers (only in dev) */
  poolModifyLiquidityTest: Address;
  poolSwapTest: Address;
}

/** Addresses deployed in Phase 5: Universal Router */
export interface UniversalRouterAddresses {
  router: Address;
}

/** Addresses deployed in Phase 6: UniswapX */
export interface UniswapXAddresses {
  exclusiveDutchOrderReactor: Address;
  priorityOrderReactor: Address;
  v2DutchOrderReactor: Address;
  orderQuoter: Address;
}

/** Addresses deployed in Phase 7: ERC-8004 Identity */
export interface ERC8004Addresses {
  identityRegistry: Address;
  reputationRegistry: Address;
}

/** Addresses deployed in Phase 8: Vault Core */
export interface VaultAddresses {
  identityAdapter: Address;
  feeModule: Address;
  vaultImplementation: Address;
  vaultFactory: Address;
}

/** Mock token addresses from seeding */
export interface MockTokenAddresses {
  weth: Address;
  usdc: Address;
  usdt: Address;
  dai: Address;
  wbtc: Address;
}

/** The complete Uniswap deployment */
export interface UniswapDeployment {
  chainId: number;
  deployer: Address;
  blockNumber: number;
  timestamp: number;
  rpcUrl?: string;
  wsUrl?: string;
  foundation: FoundationAddresses;
  v2: V2Addresses;
  v3: V3Addresses;
  v4: V4Addresses;
  universalRouter: UniversalRouterAddresses;
  uniswapX: UniswapXAddresses;
  erc8004: ERC8004Addresses;
  vault?: VaultAddresses;
  tokens?: MockTokenAddresses;
}

/** Result from a single deployment phase */
export interface DeploymentPhaseResult<T = Record<string, Address>> {
  /** Phase number (1-8) */
  phase: number;
  /** Phase name */
  name: string;
  /** Deployed addresses */
  addresses: T;
  /** Gas used for all deployments in this phase */
  gasUsed: bigint;
  /** Duration in milliseconds */
  durationMs: number;
  /** Transaction hashes */
  txHashes: Hash[];
}

/** Configuration for the devenv environment */
export interface DevenvConfig {
  /** Anvil port (0 = auto) */
  port: number;
  /** Chain ID */
  chainId: number;
  /** Fork URL (empty = fresh chain) */
  forkUrl: string;
  /** Fork block number (0 = latest) */
  forkBlockNumber: number;
  /** Whether to seed test data */
  seed: boolean;
  /** Whether to start the debug UI */
  ui: boolean;
  /** UI port */
  uiPort: number;
  /** Verbose output */
  verbose: boolean;
  /** JSON output mode (for CI) */
  json: boolean;
  /** Path to save deployment addresses */
  deploymentPath: string;
  /** Path to persist / restore Anvil state (empty = disabled) */
  state: string;
  /** Auto-launch Otterscan block explorer */
  explorer: boolean;
}

/** Default devenv configuration */
export const DEFAULT_CONFIG: DevenvConfig = {
  port: 8545,
  chainId: 31337,
  forkUrl: "",
  forkBlockNumber: 0,
  seed: true,
  ui: false,
  uiPort: 3001,
  verbose: false,
  json: false,
  deploymentPath: path.join(PACKAGE_ROOT, ".devenv-deployment.json"),
  state: "",
  explorer: false,
};

/** Parse config from environment variables and CLI args */
export function resolveConfig(
  overrides: Partial<DevenvConfig> = {},
): DevenvConfig {
  const env = process.env;
  return {
    port: overrides.port ?? parseInt(env.DEVENV_PORT ?? "8545", 10),
    chainId: overrides.chainId ?? parseInt(env.DEVENV_CHAIN_ID ?? "31337", 10),
    forkUrl: overrides.forkUrl ?? env.DEVENV_FORK_URL ?? "",
    forkBlockNumber:
      overrides.forkBlockNumber ?? parseInt(env.DEVENV_FORK_BLOCK ?? "0", 10),
    seed: overrides.seed ?? env.DEVENV_SEED !== "false",
    ui: overrides.ui ?? env.DEVENV_UI === "true",
    uiPort: overrides.uiPort ?? parseInt(env.DEVENV_UI_PORT ?? "3001", 10),
    verbose: overrides.verbose ?? env.DEVENV_VERBOSE === "true",
    json: overrides.json ?? env.DEVENV_JSON === "true",
    deploymentPath:
      overrides.deploymentPath ??
      env.DEVENV_DEPLOYMENT_PATH ??
      path.join(PACKAGE_ROOT, ".devenv-deployment.json"),
    state: overrides.state ?? env.DEVENV_STATE ?? "",
    explorer: overrides.explorer ?? env.DEVENV_EXPLORER === "true",
  };
}
